import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@/components/ui";
import { useRolesDisponibles } from "@/features/admin-usuarios/hooks/use-roles-disponibles";
import type { TipoDeSolicitudCatalogo } from "@/features/solicitudes-catalogo/services/solicitudes-catalogo.api";
import { useActualizarTipoSolicitudCatalogo } from "../hooks/use-tipos-solicitud-catalogo-admin";

/** Edición de la configuración de un tipo de solicitud de catálogo (spec §5.2). */
export function EditarTipoSolicitudDialog({
  tipo,
  onCerrar,
}: {
  tipo: TipoDeSolicitudCatalogo | null;
  onCerrar: () => void;
}) {
  const roles = useRolesDisponibles();
  const guardar = useActualizarTipoSolicitudCatalogo({ onGuardado: onCerrar });
  const [form, setForm] = useState<TipoDeSolicitudCatalogo | null>(tipo);

  useEffect(() => {
    if (tipo) setForm(tipo);
  }, [tipo]);

  if (!form) return null;
  const set = (parcial: Partial<TipoDeSolicitudCatalogo>) => setForm((f) => (f ? { ...f, ...parcial } : f));
  const alternarRol = (rol: string, marcado: boolean) =>
    set({
      rolesResponsables: marcado
        ? [...form.rolesResponsables, rol]
        : form.rolesResponsables.filter((r) => r !== rol),
    });

  const sinResponsables = form.activo && form.rolesResponsables.length === 0;
  const invalido =
    sinResponsables || !form.nombre.trim() || !form.textoBoton.trim() || !form.textoModal.trim();

  return (
    <Dialog open={tipo !== null} onOpenChange={(open) => !open && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Configurar «{form.nombre}»</DialogTitle>
        </DialogHeader>
        <Stack gap="md" className="max-h-[65vh] overflow-y-auto pr-1">
          <Switch
            label="Activo"
            description="Si está inactivo, el solicitante no ve el botón para pedir este tipo."
            checked={form.activo}
            onCheckedChange={(v) => set({ activo: v })}
          />
          <Input label="Nombre" value={form.nombre} maxLength={100} onChange={(e) => set({ nombre: e.currentTarget.value })} />
          <div>
            <Text size="sm" fw={500}>
              Roles responsables
            </Text>
            <Text size="xs" c="dimmed" className="mb-2">
              Quienes tengan alguno de estos roles verán y resolverán las solicitudes de este tipo.
            </Text>
            <div className="grid grid-cols-2 gap-2">
              {roles.map((rol) => (
                <Checkbox
                  key={rol}
                  label={rol}
                  checked={form.rolesResponsables.includes(rol)}
                  onCheckedChange={(v) => alternarRol(rol, v === true)}
                />
              ))}
            </div>
            {sinResponsables && (
              <Text size="xs" c="peligro" className="mt-1">
                Un tipo activo necesita al menos un rol responsable.
              </Text>
            )}
          </div>
          <Switch
            label="Atención automática"
            description="La solicitud se marca como atendida sola cuando el elemento se da de alta en su catálogo."
            checked={form.atencionAutomatica}
            onCheckedChange={(v) => set({ atencionAutomatica: v })}
          />
          <Switch
            label="El rechazo exige motivo"
            checked={form.rechazoExigeMotivo}
            onCheckedChange={(v) => set({ rechazoExigeMotivo: v })}
          />
          <Input
            label="Texto del botón"
            value={form.textoBoton}
            maxLength={100}
            onChange={(e) => set({ textoBoton: e.currentTarget.value })}
          />
          <Textarea
            label="Texto del modal"
            description="«{clave}» se reemplaza por el elemento pedido."
            value={form.textoModal}
            maxLength={500}
            rows={3}
            onChange={(e) => set({ textoModal: e.currentTarget.value })}
          />
        </Stack>
        <DialogFooter>
          <Button variant="default" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button disabled={invalido} loading={guardar.isPending} onClick={() => guardar.mutate(form)}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
